/**
 * A section of a details panel that folds away.
 *
 * Three lists stacked open push each other off the screen, and most of the
 * time you want one of them. They start closed, and the count sits on the
 * header - so a closed section still tells you whether it is worth opening.
 */

import { useState, type ReactNode } from 'react';
import { cn } from '@/lib/cn';
import { Icon } from '@/icons/Icon';

export function PanelFold({ label, count, icon, empty, children, onExpand }: {
  label: string;
  count: number;
  icon: string;
  empty: string;
  children: ReactNode;
  /** Offered as "see all" when there is more than the preview holds. */
  onExpand?: () => void;
}) {
  const [open, setOpen] = useState(false);

  return (
    // overflow-hidden, so the header and the body are clipped to the same
    // rounded corner instead of squaring off against it.
    <section className="overflow-hidden rounded-[var(--radius-l)] border border-line-soft bg-surface-1">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="flex w-full items-center gap-3 px-4 py-3.5 text-left transition-colors hover:bg-surface-2"
      >
        <Icon name={icon} size={16} className="flex-none text-mute" />
        {/* Sentence case, not small capitals. These are three named places to
            look, not three column headings, and tracked-out capitals made
            them read as labels for something else. */}
        <span className="min-w-0 flex-1 truncate text-[13.5px] font-semibold">
          {label}
        </span>
        {/* The count always shows, zero included - "nothing pinned" is worth
            knowing without opening the section to find out. */}
        <span className="flex-none text-[13px] font-bold text-dim tabular-nums">{count}</span>
        {/* Points down when closed: it opens downwards, so that is the way
            the arrow should be pointing before you press it. */}
        <Icon
          name="chevronD"
          size={15}
          className={cn('flex-none text-ghost transition-transform duration-200', open && 'rotate-180')}
        />
      </button>

      {open ? (
        <div className="border-t border-line-soft p-2.5">
          {count ? children : <p className="py-1 text-[12px] text-mute">{empty}</p>}
          {onExpand && count ? (
            <button
              type="button"
              onClick={onExpand}
              className="mt-2 w-full rounded-[var(--radius)] py-1.5 text-[12px] font-semibold text-accent-hi transition-colors hover:bg-surface-2"
            >
              See all {count}
            </button>
          ) : null}
        </div>
      ) : null}
    </section>
  );
}
